// src/components/accordion/AccUserFolderAssignments.tsx

import {
  AddCircleTwoTone,
  ExpandMoreTwoTone,
  FilterListTwoTone,
  RemoveCircleTwoTone,
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Collapse,
  IconButton,
  ListSubheader,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { ContractPool } from '@/types/domain/contract'
import { FolderId, FolderName, FolderPool } from '@/types/domain/folder'
import { SensorId, SensorPool } from '@/types/domain/sensor'

import { TblUserSensor } from '../'
import { getStorage } from '../../api/'
import { useContractPool, useFolderPool, useSensorPool } from '../../hooks/'

type Props = {
  setUser: (user: any | ((prev: any) => any)) => void
  user: any
  PAGE_CONFIG?: string
}

export const AccUserSensorAssignments = ({
  setUser,
  user,
  PAGE_CONFIG = 'settings_user_edit',
}: Props) => {
  const [config, setConfig] = useState<any>(getStorage(PAGE_CONFIG))

  const cPool: ContractPool = useContractPool()
  const fPool: FolderPool = useFolderPool()
  const sPool: SensorPool = useSensorPool()

  const { assignedSensorsOnly, assignmentSection } = config
  const { folders, sensors } = user

  const [sensorIdsInFolderPool, setSensorIdsInFolderPool] = useState<number[]>(
    []
  )

  useEffect(() => {
    // 1. get all sensor ids of currently selected folders
    const fIds: FolderId[] = folders.map(f => f.entity_id)

    // 2. get all folder names to update contracts accordingly to folders
    const fNames: FolderName[] = folders.map(f => f.name)

    const contractIds = cPool
      .filter(f => fNames.find(item => item === f.attributes.name))
      .map(item => item.attributes)

    setUser(prev => ({ ...prev, contracts: contractIds }))

    // 3. get all sensorIds of fIds
    const sensorIds: SensorId[] = [
      ...new Set(
        fPool
          .filter(f => fIds.some(id => Number(id) === Number(f.id)))
          .flatMap(f => f.attributes.folder_sensors)
          .map(fs => fs.sensor_id)
          .sort((a, b) => a - b)
      ),
    ]

    // 4. update state
    setSensorIdsInFolderPool(sensorIds)
    setUser(prev => ({ ...prev, sensorIds: sensorIds }))
  }, [cPool, fPool, folders, setUser])

  const onAssignAll = () => {
    const sensors = sPool
      .filter(f => sensorIdsInFolderPool.find(id => id === Number(f.id)))
      .map(item => Number(item.id))

    setUser(prev => ({ ...prev, sensors }))
  }
  const onRemoveAll = () => {
    setUser(prev => ({ ...prev, sensors: [] }))
  }
  const onToggleAssignedSensorsOnly = () => {
    setConfig(prev => ({
      ...prev,
      assignedSensorsOnly: !prev.assignedSensorsOnly,
    }))
  }

  return (
    <Collapse in={true} sx={{ mb: 2 }}>
      <Accordion
        expanded={assignmentSection === 2}
        onChange={() => setConfig(prev => ({ ...prev, assignmentSection: 2 }))}
      >
        <AccordionSummary expandIcon={<ExpandMoreTwoTone />}>
          <Typography>
            Sensoren ({sensors.length}/{sensorIdsInFolderPool.length}) in{' '}
            {folders.length} Gebäude
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <ListSubheader>
            <Tooltip title={'Alle Sensoren entziehen'}>
              <IconButton onClick={onRemoveAll}>
                <RemoveCircleTwoTone />
              </IconButton>
            </Tooltip>

            <Tooltip title={'Alle Sensoren zuweisen'}>
              <IconButton onClick={onAssignAll}>
                <AddCircleTwoTone />
              </IconButton>
            </Tooltip>

            <Tooltip title={'Nur zugewiesene'}>
              <IconButton
                sx={{ backgroundColor: assignedSensorsOnly ? '#eee' : 'none' }}
                onClick={onToggleAssignedSensorsOnly}
              >
                <FilterListTwoTone />
              </IconButton>
            </Tooltip>
          </ListSubheader>

          <TblUserSensor
            user={user}
            setUser={setUser}
            sensorIdsInFolderPool={sensorIdsInFolderPool}
          />
        </AccordionDetails>
      </Accordion>
    </Collapse>
  )
}
